// ExternalSignals — diagnostic page for ingested macro series.
//
// Research §5.4 — unemployment, gas prices, and incident volume are features
// for the churn model and context for viability scenarios. This page is the
// raw-data view so engineers can see what's ingested and when.

import { useEffect, useState } from "react";
import PageHeader from "../components/shared/PageHeader";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface SignalPoint {
  period_start: string;
  value: number;
  is_estimated: boolean;
}

interface SignalSeries {
  signal_key: string;
  source: string | null;
  unit: string | null;
  period_kind: string | null;
  points: SignalPoint[];
}

interface Response {
  range_months: number;
  signal_count: number;
  point_count: number;
  series: SignalSeries[];
  note: string | null;
}

const SIGNAL_LABELS: Record<string, string> = {
  unemployment_pwc: "PWC unemployment rate",
  gas_price_mid_atlantic: "Mid-Atlantic gas price",
  incident_volume_pwc: "PWC incident volume",
};

export default function ExternalSignals() {
  const [data, setData] = useState<Response | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/external-signals?range=36m", { credentials: "same-origin" })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<Response>)
          : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-red-600">
        Failed to load: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="External Signals · Diagnostic"
        title="Macro features"
        subtitle={
          <>
            Unemployment, gas prices, and incident volume — last{" "}
            {data.range_months} months.
          </>
        }
      />

      <section className="glass rounded-xl p-5 border border-theme-subtle/60">
        <h2 className="text-sm font-semibold text-theme-primary mb-2">
          Why this matters to the board
        </h2>
        <ul className="text-sm text-theme-secondary space-y-1.5 list-disc pl-5">
          <li>
            <strong>Unemployment</strong> is a leading indicator of volunteer
            attrition — when paid work is scarce, members lean harder on their
            day jobs and reduce volunteer hours. Sustained rises of 1+ point
            typically precede a 90-day churn lift.
          </li>
          <li>
            <strong>Gas prices</strong> are a commuting-cost proxy. Members who
            travel to drills + shifts feel rises in real dollars; the retention
            model uses this to weight at-risk scoring.
          </li>
          <li>
            <strong>Incident volume</strong> drives how often the public sees us
            at work, which feeds applicant interest. A multi-month dip is an
            early signal that recruitment top-of-funnel will soften two-to-three
            months out.
          </li>
        </ul>
        <p className="text-xs text-theme-muted mt-3 italic">
          These signals are inputs to the retention model, not standalone
          drivers. Use the trends to contextualise what the FORGE pipeline +
          retention pages are showing.
        </p>
      </section>

      {data.note && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3">
          {data.note}
        </div>
      )}

      {data.series.length === 0 && !data.note && (
        <div className="glass rounded-xl p-6 text-sm text-theme-muted">
          No signals in range.
        </div>
      )}

      {data.series.map((s) => (
        <section key={s.signal_key} className="glass rounded-2xl p-5 md:p-6">
          <header className="mb-3 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-theme-primary">
                {SIGNAL_LABELS[s.signal_key] ?? s.signal_key}
              </h2>
              <p className="text-xs text-theme-muted">
                {s.source} · {s.period_kind} · {s.unit} · {s.points.length}{" "}
                points
              </p>
            </div>
          </header>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart
                data={s.points}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  stroke="#e5e7eb"
                  strokeDasharray="3 3"
                  opacity={0.4}
                />
                <XAxis dataKey="period_start" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#2a7a6c"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      ))}
    </div>
  );
}
